import {useEffect, useMemo} from 'react';

import {useCancelableEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ApiContacts, ContactModel} from '../../../../../libs/shared/core/src/models/api/contactModel';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {
  ContactModifiers,
  ModifiersEnum,
} from '../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {getModifierPartsFromSearchQuery} from '../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {getFlatContacts} from '../../../../store/data/dataSelectors';
import {fetchTagsByAliasAction} from '../../../../store/data/tags/tagsActions';
import {getTags} from '../../../../store/data/tags/tagsSelectors';
import {allStatusDropdownItems} from '../searchBar/queryBuilder/inboxesSearchQueryBuilderHelpers';

export const getStatusModifierValues = (searchQuery: string) => {
  const modifierStatusParts = getModifierPartsFromSearchQuery(searchQuery, ModifiersEnum.IS);
  const statusesValues = allStatusDropdownItems.map((e) => e.inputValue.value);

  const selectedStatusValues = modifierStatusParts
    .map((e) => e.token.value)
    .filter((e): e is string => isDefined(e) && statusesValues.includes(e));
  return selectedStatusValues;
};

const getTagsModifierValues = (searchQuery: string) =>
  getModifierPartsFromSearchQuery(searchQuery, ModifiersEnum.TAG)
    .map((e) => e.token.value)
    .filter(isDefined);

export const useTagsFromQuery = (query: string) => {
  const selectedTagsAliases = useMemo(() => getTagsModifierValues(query), [query]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchTagsByAliasAction(selectedTagsAliases ?? []));
  }, [dispatch, selectedTagsAliases]);

  const tagsDict = useAppSelector(getTags);
  const tags = useMemo(
    () =>
      Object.values(tagsDict).filter((e): e is TagModel =>
        Boolean(e && selectedTagsAliases.includes(e.alias)),
      ),
    [tagsDict, selectedTagsAliases],
  );
  return tags;
};

const getContactsModifierValues = (searchQuery: string, modifier: ContactModifiers) =>
  getModifierPartsFromSearchQuery(searchQuery, modifier)
    .map((e) => e.token.value)
    .filter(isDefined);

export type ContactNameHandle = {
  name: string | undefined;
  handle: string;
};

const findContactFromHandle = (handle: string, contacts: ReadonlyArray<ContactModel>) =>
  contacts.find((c) => c.handle === handle);

export const useSearchCardsFromQuery = (
  query: string,
  modifier: ContactModifiers,
): ReadonlyArray<ContactNameHandle> => {
  const selectedContactsHandles = useMemo(
    () => getContactsModifierValues(query, modifier),
    [query, modifier],
  );

  const company = useAppSelector(getCompany);
  const dispatch = useAppDispatch();

  const contacts = useAppSelector(getFlatContacts);

  useCancelableEffect(
    (cancelToken) => {
      const contactHandlesWithNoCards = selectedContactsHandles.filter(
        (handle) => !findContactFromHandle(handle, contacts),
      );
      if (contactHandlesWithNoCards.length === 0) {
        return;
      }
      const url = `${company.url}/contacts_for_handles`;
      postApiAsync(
        ApiContacts,
        url,
        {
          handles: contactHandlesWithNoCards,
        },
        {cancelToken},
      ).then((fetchedContacts) => {
        dispatch(dataUpdatedAction({contacts: fetchedContacts}));
      });
    },
    [selectedContactsHandles, company, contacts, dispatch],
  );

  const searchContacts = useMemo(
    () =>
      selectedContactsHandles.map((e) => {
        const contact = findContactFromHandle(e, contacts);
        return {
          name: contact?.display_name,
          handle: e,
        };
      }),
    [selectedContactsHandles, contacts],
  );

  return searchContacts;
};
